/**
 * Admin — Delivery Zones + Serviceable Pincodes.
 * Persisted at settings.commerce.zones[] and settings.commerce.serviceable_pincodes[]
 * via PATCH /store/settings/commerce.
 *
 * Zones support: name, pincodes[], fee, free-over, min-order, ETA, express,
 * COD on/off, priority, is_active.
 */
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, MapPin, Plus, Trash2, Zap, IndianRupee, Timer } from "lucide-react";

const emptyZone = () => ({
  id: `z-${Date.now().toString(36)}`,
  name: "",
  pincodes: [],
  fee_flat: 30,
  free_over: 500,
  min_order: 199,
  eta_minutes: 30,
  express_enabled: false,
  cod_enabled: true,
  priority: 100,
  is_active: true,
});

export default function DeliveryZones() {
  const [commerceAll, setCommerceAll] = useState({});
  const [zones, setZones] = useState([]);
  const [radiusKm, setRadiusKm] = useState(5);
  const [servPins, setServPins] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/settings");
      const c = data?.settings?.commerce || {};
      setCommerceAll(c);
      setZones(Array.isArray(c.zones) ? c.zones : []);
      setRadiusKm(c.radius_km ?? data?.delivery?.radius_km ?? 5);
      setServPins((c.serviceable_pincodes || []).join(", "));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const serviceable_pincodes = servPins.split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^\d{4,7}$/.test(s));
      await api.patch("/store/settings/commerce", {
        ...commerceAll,
        zones,
        radius_km: Number(radiusKm),
        serviceable_pincodes,
      });
      toast.success("Delivery configuration saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const addZone = () => setZones((z) => [...z, emptyZone()]);
  const rmZone = (i) => setZones((z) => z.filter((_, x) => x !== i));
  const patchZone = (i, patch) => setZones((z) => z.map((row, x) => (x === i ? { ...row, ...patch } : row)));
  const setPinList = (i, str) => {
    const pincodes = str.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    patchZone(i, { pincodes });
  };

  return (
    <div className="p-8 space-y-6" data-testid="delivery-zones-page">
      <PageHeader
        title="Delivery Zones"
        subtitle="Serviceable areas, zone-level charges, minimum order, ETA and COD toggles."
        actions={
          <Button onClick={save} disabled={saving || loading} className="btn-primary-glow" data-testid="dz-save">
            <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save changes"}
          </Button>
        }
      />

      {/* Global */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Serviceable area</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Serviceable pincodes</Label>
            <Textarea
              rows={3}
              value={servPins}
              onChange={(e) => setServPins(e.target.value)}
              placeholder="560001, 560002, 560103, 560068"
              data-testid="dz-pins"
            />
            <div className="text-[11px] text-muted-foreground mt-1">Comma or space separated. Only 4–7 digit pincodes are saved.</div>
          </div>
          <div>
            <Label>Delivery radius (km)</Label>
            <div className="grid grid-cols-3 gap-1 mt-1">
              {[1, 2, 3, 5, 10].map((r) => (
                <button key={r} type="button" onClick={() => setRadiusKm(r)} className={`rounded-lg border py-2 text-sm font-semibold transition ${Number(radiusKm) === r ? "border-primary bg-primary/5 text-primary" : "hover:bg-secondary/40"}`} data-testid={`dz-radius-${r}`}>{r} km</button>
              ))}
              <Input type="number" min="1" step="0.5" value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)} className="col-span-3 mt-1" data-testid="dz-radius" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zones */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Zones ({zones.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={addZone} data-testid="dz-add-zone"><Plus className="w-4 h-4 mr-1" />Add zone</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-32 rounded-lg bg-secondary/40 animate-pulse" />
          ) : zones.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              No zones yet. Add a zone to charge different delivery fees per area.
            </div>
          ) : (
            <div className="space-y-4">
              {zones.map((z, i) => (
                <div key={z.id || i} className="rounded-xl border p-4 md:p-5 bg-card" data-testid={`dz-zone-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-primary hover:bg-primary">Zone #{i + 1}</Badge>
                      {z.express_enabled && <Badge variant="outline" className="border-amber-500 text-amber-700"><Zap className="w-3 h-3 mr-1" />Express</Badge>}
                      {!z.is_active && <Badge variant="secondary">Inactive</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!z.is_active} onCheckedChange={(v) => patchZone(i, { is_active: v })} data-testid={`dz-active-${i}`} />
                      <Button size="icon" variant="ghost" onClick={() => rmZone(i)} data-testid={`dz-rm-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <Label>Zone name</Label>
                      <Input value={z.name} onChange={(e) => patchZone(i, { name: e.target.value })} placeholder="e.g. Koramangala" data-testid={`dz-name-${i}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Pincodes covered</Label>
                      <Textarea
                        rows={2}
                        value={(z.pincodes || []).join(", ")}
                        onChange={(e) => setPinList(i, e.target.value)}
                        placeholder="560034, 560095"
                        data-testid={`dz-pins-${i}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Delivery fee (₹)</Label>
                      <div className="relative">
                        <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input type="number" min="0" className="pl-7" value={z.fee_flat} onChange={(e) => patchZone(i, { fee_flat: Number(e.target.value) })} data-testid={`dz-fee-${i}`} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Free above (₹)</Label>
                      <Input type="number" min="0" value={z.free_over} onChange={(e) => patchZone(i, { free_over: Number(e.target.value) })} data-testid={`dz-free-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Min order (₹)</Label>
                      <Input type="number" min="0" value={z.min_order} onChange={(e) => patchZone(i, { min_order: Number(e.target.value) })} data-testid={`dz-min-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">ETA (minutes)</Label>
                      <div className="relative">
                        <Timer className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input type="number" min="1" className="pl-7" value={z.eta_minutes} onChange={(e) => patchZone(i, { eta_minutes: Number(e.target.value) })} data-testid={`dz-eta-${i}`} />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="flex items-center gap-2 rounded-lg border px-3 py-2">
                      <Switch checked={!!z.express_enabled} onCheckedChange={(v) => patchZone(i, { express_enabled: v })} data-testid={`dz-express-${i}`} />
                      <Label className="text-sm">Express delivery</Label>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg border px-3 py-2">
                      <Switch checked={z.cod_enabled !== false} onCheckedChange={(v) => patchZone(i, { cod_enabled: v })} data-testid={`dz-cod-${i}`} />
                      <Label className="text-sm">Cash on delivery</Label>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Priority</Label>
                      <Input type="number" min="1" value={z.priority} onChange={(e) => patchZone(i, { priority: Number(e.target.value) })} data-testid={`dz-priority-${i}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
